import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckMarkFillLegacy, MoreVertical } from '@lyearn/icons';
import { LargeActionMenu } from '@lyearn/molecules';
import { Avatar, CircularProgress, Stack, Typography } from '@lyearn/ui';
import { UserType } from '@/types/schema';

import ForceCompleteAlert from './ForceCompleteAlert';
import SyncProgressAlert from './SyncProgressAlert';
import useJourneyViewProgressActions from './useJourneyViewProgressActions';
type UserListItemProps = {
  user: Pick<UserType, 'name' | 'profilePicture' | 'id' | 'emailId'>;
  progress?: number;
  entityId: string;
  batchId: string;
};

const UserListItem = (props: UserListItemProps) => {
  const { user, progress, entityId, batchId } = props;
  const { t } = useTranslation();
  const [forceCompleteAlert, { on: showForceCompleteAlert, off: closeForceCompleteAlert }] =
    useToggle();
  const [syncProgressAlert, { on: showSyncProgressAlert, off: closeSyncProgressAlert }] =
    useToggle();

  const { items, onAction, forceCompleteContent, syncUserContentProgress, maxProgress } =
    useJourneyViewProgressActions({
      userId: user.id,
      userName: user?.name || '',
      entityId,
      showForceCompleteAlert,
      showSyncProgressAlert,
      closeForceCompleteAlert,
      closeSyncProgressAlert,
      batchId,
    });

  return (
    <>
      <Stack className="flex w-full py-16 h-full" direction="row">
        <Stack className="w-full justify-between" direction="row">
          <Stack>
            <Avatar size="xx-large" src={user.profilePicture ?? ''} />
            <Stack className="pl-10" direction="col">
              <Typography variant="body-short-01">{user.name}</Typography>
              <Typography className="text-text-secondary" variant="body-short-02">
                {user.emailId}
              </Typography>
            </Stack>
          </Stack>
          <Stack alignItems="center" direction="row">
            {progress === 100 ? (
              <Stack alignItems="center" direction="row">
                <CheckMarkFillLegacy fillColorToken="icon-success" height={20} width={20} />
                <Typography className="text-text-secondary pl-4" variant="body-short-02">
                  {t('Common:Filter.Completed')}
                </Typography>
              </Stack>
            ) : (
              <Stack alignItems="center" direction="row">
                <CircularProgress color="success" size="x-small" value={progress} />
                <Typography className="pl-8 text-text-secondary" variant="body-short-02">
                  {Math.round((progress ?? 0) * 100) / 100}%
                </Typography>
              </Stack>
            )}
            <LargeActionMenu items={items} onAction={onAction}>
              <MoreVertical
                className="ml-12 cursor-pointer"
                fillColorToken="icon-placeholder"
                height={20}
                width={20}
              />
            </LargeActionMenu>
          </Stack>
        </Stack>
      </Stack>
      <ForceCompleteAlert
        name={user.name || ''}
        open={forceCompleteAlert}
        onClick={() => forceCompleteContent()}
        onClose={closeForceCompleteAlert}
      />
      <SyncProgressAlert
        name={user.name || ''}
        open={syncProgressAlert}
        progress={maxProgress}
        onClick={() => syncUserContentProgress()}
        onClose={closeSyncProgressAlert}
      />
    </>
  );
};

export default UserListItem;
